import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { useSettings, waLink, telLink } from "../../context/SettingsContext";
import { Phone, MessageCircle, Trash2, RefreshCw, CheckCircle2, Clock, AlertCircle } from "lucide-react";

const STATUSES = ["New", "Contacted", "Follow-Up", "Converted", "Closed"];

function CrmBadge({ lead, onRetry, busy }) {
  const s = lead.crm_sync_status || "Pending";
  const map = {
    Synced: { icon: CheckCircle2, cls: "bg-green-50 text-green-700 border-green-200" },
    Failed: { icon: AlertCircle, cls: "bg-red-50 text-red-600 border-red-200" },
    Pending: { icon: Clock, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  };
  const { icon: Icon, cls } = map[s] || map.Pending;
  const label = s === "Synced" && lead.crm_action === "note_added" ? "CRM: Note added" : `CRM: ${s}`;
  return (
    <div className="flex items-center gap-2 mt-2" data-testid={`lead-crm-${lead.id}`}>
      <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border ${cls}`} title={lead.crm_last_error || ""}>
        <Icon size={11} /> {label}
      </span>
      {s !== "Synced" && (
        <button onClick={() => onRetry(lead.id)} disabled={busy} data-testid={`lead-crm-retry-${lead.id}`}
          className="inline-flex items-center gap-1 text-[11px] text-ink border border-line rounded-full px-2 py-0.5 hover:bg-sand disabled:opacity-50">
          <RefreshCw size={11} className={busy ? "animate-spin" : ""} /> Retry
        </button>
      )}
      {lead.crm_last_error && s === "Failed" && <span className="text-[10px] text-red-500 truncate max-w-[220px]">{lead.crm_last_error}</span>}
    </div>
  );
}

export default function LeadsAdmin() {
  const { settings } = useSettings();
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = () => api.get(`/leads/admin/all${filter ? `?status=${filter}` : ""}`).then((r) => setLeads(r.data));
  useEffect(() => { load(); }, [filter]);

  const setStatus = async (id, status) => { await api.patch(`/leads/admin/${id}`, { status }); load(); };
  const del = async (id) => { if (window.confirm("Delete this lead?")) { await api.delete(`/leads/admin/${id}`); load(); } };
  const retry = async (id) => {
    setBusyId(id);
    try { await api.post(`/leads/admin/${id}/retry-sync`); await new Promise((r) => setTimeout(r, 1500)); await load(); }
    finally { setBusyId(""); }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-ink mb-4">Leads <span className="text-muted text-base">({leads.length})</span></h1>
      <div className="flex gap-2 flex-wrap mb-5">
        <button onClick={() => setFilter("")} className={`px-4 py-2 rounded-full text-sm border ${filter === "" ? "bg-ink text-white border-ink" : "border-line"}`}>All</button>
        {STATUSES.map((s) => <button key={s} onClick={() => setFilter(s)} className={`px-4 py-2 rounded-full text-sm border ${filter === s ? "bg-ink text-white border-ink" : "border-line"}`}>{s}</button>)}
      </div>

      <div className="space-y-3">
        {leads.length === 0 && <p className="text-muted">No leads yet.</p>}
        {leads.map((l) => (
          <div key={l.id} className="rounded-xl2 bg-white border border-line p-4 md:p-5" data-testid={`lead-${l.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-bold text-ink">{l.name} <span className="text-muted font-normal text-sm">· {l.phone}</span></div>
                  {l.source_page && <span className="text-[10px] font-bold uppercase tracking-wide bg-gold/15 text-gold-dark px-2 py-0.5 rounded-full" data-testid={`lead-source-badge-${l.id}`}>{l.source_page}</span>}
                </div>
                <div className="text-xs text-muted mt-1">{l.service || "General"} · {l.city || "—"} {l.plot_size ? `· ${l.plot_size}` : ""} {l.facing ? `· ${l.facing}` : ""} {l.bhk ? `· ${l.bhk}` : ""}</div>
                {(l.budget || l.timeline) && <div className="text-xs text-muted mt-0.5">Budget: {l.budget || "—"} · Timeline: {l.timeline || "—"}</div>}
                {l.source_project && <div className="text-xs text-ink mt-0.5">Interested project: <span className="font-semibold">{l.source_project}</span>{l.source_project_category ? ` (${l.source_project_category})` : ""}</div>}
                {l.message && <p className="text-sm text-ink/70 mt-2">{l.message}</p>}
                <div className="text-[11px] text-muted mt-2">{new Date(l.created_at).toLocaleString()}{l.utm_source ? ` · UTM: ${l.utm_source}` : ""}</div>
                <CrmBadge lead={l} onRetry={retry} busy={busyId === l.id} />
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a href={telLink(l.phone)} className="w-9 h-9 rounded-full bg-ink text-white flex items-center justify-center" title="Call"><Phone size={15} /></a>
                <a href={waLink(l.phone, `Hello ${l.name}, this is ${settings.company_name}.`)} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-[#25D366] text-white flex items-center justify-center" title="WhatsApp"><MessageCircle size={15} /></a>
                <select value={l.status} onChange={(e) => setStatus(l.id, e.target.value)} data-testid={`lead-status-${l.id}`} className="rounded-lg border border-line text-sm px-2 py-2">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
                <button onClick={() => del(l.id)} className="w-9 h-9 rounded-full border border-line text-red-500 flex items-center justify-center"><Trash2 size={15} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
